'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronDown } from 'lucide-react';
import { useSiteLogo, FALLBACK_LOGO } from '@/lib/useSiteLogo';

const navItems = [
  {
    label: 'Solutions',
    href: '#solutions',
    children: [
      { label: 'Budgeting & Forecasting', href: '#solutions' },
      { label: 'Management Accounts', href: '#solutions' },
      { label: 'Performance Appraisal', href: '#solutions' },
      { label: 'Data Analytics', href: '#solutions' },
      { label: 'Advanced Excel Training', href: '#solutions' },
      { label: 'Uniform Fuel Pricing', href: '#solutions' },
    ],
  },
  { label: 'About', href: '#about' },
  { label: 'Process', href: '#process' },
  { label: 'Engagement Guide', href: '/engagement-guide' },
  { label: 'News', href: '#news' },
  { label: 'Contact', href: '#contact' },
];

export default function PublicTopbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { logoUrl, isCustomLogo, logoMaxWidth, loading } = useSiteLogo();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    // Set initial state
    setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? 'rgba(255,255,255,0.98)' : 'rgba(255,255,255,0.0)',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(30,82,94,0.12)' : '1px solid transparent',
        boxShadow: scrolled ? '0 2px 30px rgba(0,0,0,0.08)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[72px]">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-3 shrink-0">
          <img
            src={logoUrl || FALLBACK_LOGO}
            alt="Celsoft Corporation logo"
            className="object-contain"
            style={{
              maxWidth: logoMaxWidth,
              maxHeight: 52,
              width: 'auto',
              height: 'auto',
              visibility: loading ? 'hidden' : 'visible',
            }}
          />
        </Link>

        {/* Desktop nav */}
        <nav
          className="hidden lg:flex items-center gap-1 px-2 py-1 transition-all duration-300"
          style={{
            background: scrolled ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.15)',
            border: scrolled ? '1px solid rgba(30,82,94,0.12)' : '1px solid rgba(255,255,255,0.3)',
            borderRadius: '100px',
            backdropFilter: 'blur(8px)',
          }}
        >
          {navItems?.map((item) =>
            item?.children ? (
              <div
                key={`nav-${item?.label}`}
                className="relative"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <button
                  className="flex items-center gap-1.5 px-4 py-2 text-[14px] font-medium transition-all duration-200 rounded-full"
                  style={{ color: scrolled ? '#52525B' : '#ffffff' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = scrolled ? '#111111' : '#ffffff';
                    (e.currentTarget as HTMLButtonElement).style.background = scrolled ? 'rgba(30,82,94,0.06)' : 'rgba(255,255,255,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = scrolled ? '#52525B' : '#ffffff';
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  {item?.label}
                  <ChevronDown size={13} className={`transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {dropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-2 w-64 py-2"
                    style={{
                      background: '#ffffff',
                      borderRadius: '20px',
                      boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
                      border: '1px solid rgba(30,82,94,0.12)',
                    }}
                  >
                    {item?.children?.map((child) => (
                      <Link
                        key={`nav-child-${child?.label}`}
                        href={child?.href}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
                        style={{ color: '#52525B' }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLAnchorElement).style.color = '#1e525e';
                          (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(30,82,94,0.04)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLAnchorElement).style.color = '#52525B';
                          (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ background: '#8dc53e' }}
                        />
                        {child?.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={`nav-${item?.label}`}
                href={item?.href}
                className="px-4 py-2 text-[14px] font-medium transition-all duration-200 rounded-full"
                style={{ color: scrolled ? '#52525B' : '#ffffff' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color = scrolled ? '#111111' : '#ffffff';
                  (e.currentTarget as HTMLAnchorElement).style.background = scrolled ? 'rgba(30,82,94,0.06)' : 'rgba(255,255,255,0.2)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color = scrolled ? '#52525B' : '#ffffff';
                  (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                }}
              >
                {item?.label}
              </Link>
            )
          )}
        </nav>

        {/* CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <Link
            href="/sign-up-login-screen"
            className="px-4 py-2 text-sm font-medium rounded-full transition-all duration-200"
            style={{ color: scrolled ? '#52525B' : '#ffffff' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = scrolled ? '#111111' : '#ffffffcc'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = scrolled ? '#52525B' : '#ffffff'; }}
          >
            Sign In
          </Link>
          <Link
            href="/client-portal"
            className="px-5 py-2.5 text-sm rounded-full font-medium transition-all duration-200"
            style={{
              background: scrolled ? '#1e525e' : 'rgba(255,255,255,0.2)',
              color: '#ffffff',
              border: scrolled ? 'none' : '1px solid rgba(255,255,255,0.5)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = scrolled ? '#174048' : 'rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = scrolled ? '#1e525e' : 'rgba(255,255,255,0.2)';
            }}
          >
            Client Area
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 rounded-xl transition-colors"
          style={{ color: scrolled ? '#52525B' : '#ffffff' }}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle mobile menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {mobileOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="lg:hidden"
          style={{
            background: '#ffffff',
            borderTop: '1px solid rgba(30,82,94,0.1)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
          }}
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {navItems?.map((item) => (
              <Link
                key={`mob-nav-${item?.label}`}
                href={item?.children ? item?.children?.[0]?.href : item?.href}
                className="px-3 py-2.5 text-sm font-medium rounded-xl transition-colors"
                style={{ color: '#52525B' }}
                onClick={() => setMobileOpen(false)}
              >
                {item?.label}
              </Link>
            ))}
            <div
              className="mt-3 pt-3 flex flex-col gap-2"
              style={{ borderTop: '1px solid rgba(30,82,94,0.1)' }}
            >
              <Link
                href="/sign-up-login-screen"
                className="block text-center px-4 py-2.5 text-sm font-medium rounded-full transition-all"
                style={{ border: '1px solid rgba(30,82,94,0.25)', color: '#1e525e' }}
                onClick={() => setMobileOpen(false)}
              >
                Sign In
              </Link>
              <Link
                href="/client-portal"
                className="block text-center px-4 py-2.5 text-sm rounded-full font-medium"
                style={{ background: '#1e525e', color: '#ffffff' }}
                onClick={() => setMobileOpen(false)}
              >
                Client Area
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
